import * as moment from 'moment';
import {KEYS} from '../../../translation-keys';

/** A restricted expression reader. Never executes workflow expressions as code. */
export type ConditionNode =
  {kind: 'group'; operator: '&&' | '||'; children: ConditionNode[]} |
  {kind: 'rule'; variable: string; operator: string; value: string | number | boolean};
export interface ConditionAnalysis {
  root: ConditionNode | null;
  references: string[];
  count: number;
  error: boolean;
}

/** Parses supported comparisons and logical groups while rejecting malformed input. */
export function analyseCondition(expression: unknown): ConditionAnalysis {
  const empty = {root: null, references: [], count: 0, error: false};
  if (!expression || (Array.isArray(expression) && !expression.length)) { return empty; }
  if (typeof expression !== 'string' || expression.length > 20000) { return {...empty, error: true}; }
  if (!expression.trim()) { return empty; }
  try {
    const tokens: string[] = [];
    const token = /\s*("(?:[^"\\]|\\.)*"|&&|\|\||==|!=|>=|<=|>|<|[()]|[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?|[A-Za-z_$][A-Za-z0-9_$]*)/y;
    let offset = 0;
    while (offset < expression.trimEnd().length) {
      token.lastIndex = offset;
      const match = token.exec(expression);
      if (!match) { throw new Error('Unsupported expression'); }
      tokens.push(match[1]);
      offset = token.lastIndex;
    }
    let position = 0;
    let count = 0;
    const references = new Set<string>();
    const read = (depth: number): ConditionNode => {
      if (depth > 64) { throw new Error('Expression nesting limit'); }
      if (tokens[position] === '(') {
        position++;
        const group = or(depth + 1);
        if (tokens[position++] !== ')') { throw new Error('Unclosed group'); }
        return group;
      }
      const variable = tokens[position++];
      const operator = tokens[position++];
      const literal = tokens[position++];
      if (!/^[A-Za-z_$][A-Za-z0-9_$]*$/.test(variable || '') ||
          !['==', '!=', '>', '<', '>=', '<='].includes(operator) || !literal) {
        throw new Error('Invalid comparison');
      }
      let value: string | boolean | number;
      if (literal[0] === '"') { value = JSON.parse(literal); }
      else if (literal === 'true' || literal === 'false') { value = literal === 'true'; }
      else {
        value = Number(literal);
        if (!Number.isFinite(value)) { throw new Error('Invalid literal'); }
      }
      references.add(variable);
      count++;
      return {kind: 'rule', variable, operator, value};
    };
    const and = (depth: number): ConditionNode => {
      const children = [read(depth)];
      while (tokens[position] === '&&') { position++; children.push(read(depth)); }
      return children.length === 1 ? children[0] : {kind: 'group', operator: '&&', children};
    };
    const or = (depth: number): ConditionNode => {
      const children = [and(depth)];
      while (tokens[position] === '||') { position++; children.push(and(depth)); }
      return children.length === 1 ? children[0] : {kind: 'group', operator: '||', children};
    };
    const root = or(0);
    if (position !== tokens.length) { throw new Error('Unexpected trailing expression'); }
    return {root, references: Array.from(references), count, error: false};
  } catch {
    return {...empty, error: true};
  }
}

/** Describes a condition with localized operators and explicit nested groups. */
export function describeCondition(node: ConditionNode, translate: (text: string) => string,
  formatDate: (variable: string, value: string | number | boolean) => string | null = () => null): string {
  if (node.kind === 'group') {
    return node.children.map(child => child.kind === 'group'
      ? '(' + describeCondition(child, translate, formatDate) + ')' : describeCondition(child, translate, formatDate))
      .join(' ' + translate(node.operator === '&&' ? KEYS.CRUD.AND : KEYS.CRUD.OR) + ' ');
  }
  const operators = {'==': KEYS.CRUD.EQUALS, '!=': KEYS.CRUD.NOT_EQUALS, '>': KEYS.CRUD.GREATER_THAN,
    '<': KEYS.CRUD.LESS_THAN, '>=': KEYS.CRUD.AT_LEAST, '<=': KEYS.CRUD.AT_MOST};
  const date = formatDate(node.variable, node.value);
  if (date !== null) {
    return node.variable + ' ' + translate(operators[node.operator]) + ' ' + date;
  }
  const value = typeof node.value === 'boolean' ? translate(node.value ? KEYS.CRUD.YES : KEYS.CRUD.NO) :
    typeof node.value === 'string' ? '„' + node.value + '“' : String(node.value);
  return node.variable + ' ' + translate(operators[node.operator]) + ' ' + value;
}

/** Formats declared date variables using application locale formats without shifting calendar dates. */
export function formatConditionDate(value: string | number | boolean, variable: any,
  dateFormat: (token: string) => string): string | null {
  if (variable?.target?.options?.format !== 'date' || typeof value === 'boolean') { return null; }
  const raw = String(value);
  if (!/^\d{4}(?:-\d{2}(?:-\d{2}(?:T.*)?)?)?$/.test(raw)) { return null; }
  const date = moment.parseZone(raw, moment.ISO_8601, true);
  if (!date.isValid()) { return null; }
  const token = raw.includes('T') ? 'DD.MM.YYYY HH:mm' : raw.length === 10 ? 'DD.MM.YYYY' :
    raw.length === 7 ? 'MM.YYYY' : 'YYYY';
  return date.format(dateFormat(token));
}

export type TestValue = string | number | boolean;
export interface ConditionTrace { rule: ConditionNode; value?: TestValue; result: boolean | null; reason?: string; }
export interface StepTestResult {
  index: number;
  result: boolean | null;
  reason: string;
  traces: ConditionTrace[];
  before: Record<string, TestValue>;
  clientValues: Record<string, TestValue>;
  derivedValues: Record<string, TestValue>;
  after: Record<string, TestValue>;
  changes: {name: string; previous?: TestValue; value: TestValue}[];
}

/** Uses the primitive comparison semantics of the server, with no eval or Function. */
export function evaluateCondition(node: ConditionNode,
  resolve: (name: string) => {value?: TestValue; reason?: string}, traces: ConditionTrace[]): boolean | null {
  if (node.kind === 'group') {
    for (const child of node.children) {
      const value = evaluateCondition(child, resolve, traces);
      if (value === null) { return null; }
      if (node.operator === '&&' && !value) { return false; }
      if (node.operator === '||' && value) { return true; }
    }
    return node.operator === '&&';
  }
  const resolved = resolve(node.variable);
  if (resolved.reason || resolved.value === undefined) {
    traces.push({rule: node, result: null, reason: resolved.reason || KEYS.CRUD.MISSING_EXAMPLE});
    return null;
  }
  const left: any = resolved.value;
  const right: any = node.value;
  let result: boolean;
  // intentionally mirrors JavaScript's loose comparisons used by the workflow API.
  switch (node.operator) {
    case '==': result = left == right; break;
    case '!=': result = left != right; break;
    case '>': result = left > right; break;
    case '<': result = left < right; break;
    case '>=': result = left >= right; break;
    case '<=': result = left <= right; break;
    default: return null;
  }
  traces.push({rule: node, value: resolved.value, result});
  return result;
}

/** Describes one simulated submission value and its workflow targets. */
export interface TestAssignment { names: string[]; value: TestValue; source?: string; }

/** Checks the same source path that the server reads from submission variables. */
export function questionnaireOffersVariable(questionnaire: any, source: string): boolean {
  const [scope, name] = (source || '').split('.');
  return !!scope && !!name && Object.prototype.hasOwnProperty.call(questionnaire?.meta?.options?.variables?.[scope] || {}, name);
}

/** Lists source fields needed to generate a patient variable in the mobile filling app. */
export function clientVariableDependencies(source: string): string[] {
  switch (source) {
    case 'client.age': case 'client.ageInMonths': return ['client.birthDate'];
    case 'client.salutation': case 'client.isMale': case 'client.isFemale': case 'client.isDiverse': return ['client.gender'];
    case 'client.name': return ['client.firstName', 'client.lastName'];
    default: return [];
  }
}

/** Derives mobile client values while preserving every explicitly supplied test value. */
export function deriveClientTestValues(explicit: Record<string, TestValue>, translate: (text: string) => string,
  today = moment()): {values: Record<string, TestValue>; derived: Record<string, TestValue>} {
  const derived: Record<string, TestValue> = {};
  const birthDate = explicit['client.birthDate'];
  if (typeof birthDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(birthDate)) {
    const date = moment(birthDate, 'YYYY-MM-DD', true);
    if (date.isValid()) {
      derived['client.age'] = today.diff(date, 'years');
      derived['client.ageInMonths'] = today.diff(date, 'months');
    }
  }
  let gender = explicit['client.gender'];
  if (explicit['client.isMale'] === true) { gender = 'male'; }
  if (explicit['client.isFemale'] === true) { gender = 'female'; }
  if (explicit['client.isDiverse'] === true) { gender = 'diverse'; }
  if (['male', 'female', 'diverse'].includes(String(gender))) {
    derived['client.gender'] = gender;
    derived['client.isMale'] = gender === 'male';
    derived['client.isFemale'] = gender === 'female';
    derived['client.isDiverse'] = gender === 'diverse';
    derived['client.salutation'] = gender === 'male' ? translate(KEYS.CRUD.TEST_MR) :
      gender === 'female' ? translate(KEYS.CRUD.TEST_MS) : '';
  }
  const firstName = explicit['client.firstName'];
  const lastName = explicit['client.lastName'];
  if (firstName && lastName) { derived['client.name'] = firstName + ' ' + lastName; }
  derived['client.anonymous'] = false;
  derived['client.pseudonymous'] = false;
  Object.keys(explicit).forEach(source => delete derived[source]);
  return {values: {...derived, ...explicit}, derived};
}

/** Applies submission values only after an executed step, before the next condition. */
export function simulateWorkflow(workflow: any, submissions: TestAssignment[][],
  translate: (text: string) => string = text => text, today = moment()): StepTestResult[] {
  const values = new Map<string, TestValue>();
  const variables: any[] = workflow.variables || [];
  let blocked = false;
  const clientExplicit: Record<string, TestValue> = {};
  let client = {values: {} as Record<string, TestValue>, derived: {} as Record<string, TestValue>};
  return (workflow.stages || []).map((stage: any, index: number) => {
    const before = Object.fromEntries(values);
    const changes: StepTestResult['changes'] = [];
    const traces: ConditionTrace[] = [];
    let result: boolean | null = true;
    let reason = KEYS.CRUD.NO_CONDITION;
    const analysis = analyseCondition(stage.expression);
    if (blocked) { result = null; reason = KEYS.CRUD.CHECK_PREVIOUS_STEP; }
    else if (index === 0) { reason = KEYS.CRUD.FIRST_ALWAYS_RUNS; }
    else if (analysis.error) { result = null; reason = KEYS.CRUD.INVALID_CONDITION; }
    else if (analysis.root) {
      result = evaluateCondition(analysis.root, name => {
        if (!variables.some(v => v.name === name)) { return {reason: KEYS.CRUD.MISSING_VARIABLE}; }
        if (!values.has(name)) { return {reason: KEYS.CRUD.MISSING_EXAMPLE}; }
        return {value: values.get(name)};
      }, traces);
      reason = result === null ? KEYS.CRUD.CONDITION_UNDETERMINED :
        result ? KEYS.CRUD.TEST_CONDITION_MET : KEYS.CRUD.TEST_CONDITION_NOT_MET;
    }
    if (result === true && !(stage.questionnaires || []).length) {
      result = null;
      reason = KEYS.CRUD.NO_QUESTIONNAIRES;
    }
    if (result === true) {
      for (const submission of submissions[index] || []) {
        if (submission.source?.startsWith('client.')) { clientExplicit[submission.source] = submission.value; }
        for (const name of submission.names) {
          if (!variables.some(v => v.name === name)) { continue; }
          const previous = values.get(name);
          values.set(name, submission.value);
          if (previous !== submission.value) { changes.push({name, previous, value: submission.value}); }
        }
      }
      client = deriveClientTestValues(clientExplicit, translate, today);
      for (const variable of variables) {
        const source = variable.target?.variable;
        if (variable.target?.type !== 'survey' || !source?.startsWith('client.')) { continue; }
        const value = client.values[source];
        if (value === undefined) { values.delete(variable.name); continue; }
        values.set(variable.name, value);
      }
      changes.length = 0;
      for (const [name, value] of values) {
        if (before[name] !== value) { changes.push({name, previous: before[name], value}); }
      }
    }
    if (result === null) { blocked = true; }
    return {index, result, reason, traces, before, after: Object.fromEntries(values), changes,
      clientValues: {...client.values}, derivedValues: {...client.derived}};
  });
}
